import { BuildOutlined } from '@ant-design/icons';
import {
  Archive,
  ArrowUpNarrowWide,
  Asterisk,
  AtSign,
  BookUser,
  Building2,
  ChartNoAxesCombined,
  CircleStar,
  Globe,
  Hash,
  Headset,
  Heart,
  LayoutList,
  Package,
  ScanSearch,
  Tag as TagIcon,
  TicketCheck,
  User,
  Workflow,
  Zap,
} from 'lucide-react';

import { Flex } from '@/components/base';
import { MainText } from '@/components/Typography/NamedTypo';
import IconTextProperty from '@/features/Commons/Label/IconTextProperty';
import Label from '@/features/Feedback/field/Tag/Label';
import TagOptionNode from '@/features/Feedback/field/Tag/TagOptionNode';
import useTaxonomyTopicIcon from '@/features/Taxonomy/hooks/useTaxonomyTopicIcon';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import useCategoryMap from '@/hooks/domain/useCategoryMap';
import service from '@/services';
import { useFeedbackLanguageList } from '@/stores/LocaleStore';
import useTagStore from '@/stores/TagStore';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { type CATEGORY_TYPE, OrderedCategoryList } from '@/types/categoryConst';
import {
  adPredictionList,
  FEEDBACK_STATUS,
  priorityMap,
  SOURCE_STATUS,
} from '@/types/feedbackConst';
import { FEEDBACK_COLUMNS, SOURCE_COLUMNS } from '@/types/filterKeyConst';
import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import feedbackUtils from '@/utils/feedback';
import { getLv1TagNodes, getTagPath } from '@/utils/tag';

import { RangeFilter } from '../components/RangeFilter';
import { StructuredKeywordFilterInput } from '../components/structured-keyword-filter-input';
import {
  StructuredHashtagFilterInput,
  StructuredMentionFilterInput,
} from '../components/StructuredTagFilterInput';
import { useAccountFilterMap } from './useAccountFilterMap';
import { useAgentFilterMap } from './useAgentFilterMap';
import { useContactFilterMap } from './useContactFilterMap';
import { useDemographicFilterMap } from './useDemographicFilterMap';
import { useEngagementFilterMap } from './useEngagementFilterMap';
import { useProductFilterMap } from './useProductFilterMap';
import { useScoreFilterMap } from './useScoreFilterMap';
import { useSentimentFilterMap } from './useSentimentFilterMap';
import { useSourceFilterMap } from './useSourceFilterMap';

const useStructuredFilterMap = () => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const savedTaxonomyId = currWorkspace?.saved_taxonomy_id ?? null;

  const taxonomyMap = useTaxonomyStore((state) => state.topicTreeMap);
  const fullTopicMap = useTaxonomyStore((state) => state.fullTopicMap);
  const tagMap = useTagStore((state) => state.tagMap);
  const getIcon = useTaxonomyTopicIcon();

  const currentCategoryMap = useCategoryMap();

  const contactFilterMap = useContactFilterMap();
  const accountFilterMap = useAccountFilterMap();
  const agentFilterMap = useAgentFilterMap();
  const demographicFilterMap = useDemographicFilterMap();
  const sentimentFilterMap = useSentimentFilterMap();
  const scoreFilterMap = useScoreFilterMap();
  const engagementFilterMap = useEngagementFilterMap();
  const sourceFilterMap = useSourceFilterMap();
  const productFilterMap = useProductFilterMap();
  const languageList = useFeedbackLanguageList();
  const sourceTypeField = sourceFilterMap[SOURCE_COLUMNS.SOURCE_TYPE];

  const fieldOptions: StructuredFieldOptionMap = {
    [SOURCE_COLUMNS.SOURCE_TYPE]: {
      ...sourceTypeField,
      icon: <Workflow />,
    },
    [FEEDBACK_COLUMNS.CUSTOMER]: {
      icon: <User />,
      columnKey: FEEDBACK_COLUMNS.CUSTOMER,
      columnName: 'Contact',
      isMultiValueField: true,
      childField: contactFilterMap,
    },
    [FEEDBACK_COLUMNS.COMPNAY]: {
      icon: <Building2 />,
      columnKey: FEEDBACK_COLUMNS.COMPNAY,
      columnName: 'Account',
      isMultiValueField: true,
      childField: accountFilterMap,
    },
    [FEEDBACK_COLUMNS.AGENT]: {
      icon: <Headset />,
      columnKey: FEEDBACK_COLUMNS.AGENT,
      columnName: 'Agent',
      isMultiValueField: true,
      childField: agentFilterMap,
    },
    [FEEDBACK_COLUMNS.DEMOGRAPHIC]: {
      icon: <BookUser />,
      columnKey: FEEDBACK_COLUMNS.DEMOGRAPHIC,
      columnName: 'Demographic',
      isMultiValueField: true,
      childField: demographicFilterMap,
    },
    [FEEDBACK_COLUMNS.TAG]: {
      icon: <TagIcon />,
      columnKey: FEEDBACK_COLUMNS.TAG,
      columnName: 'Tag',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'tree',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          if (searchValue) {
            return Object.keys(tagMap)
              .map((alias) => {
                const node = tagMap[alias];
                const paths = getTagPath(tagMap, alias).map((p) => p.name);
                const parents = paths.slice(0, -1);
                return {
                  columnKey: FEEDBACK_COLUMNS.TAG,
                  filterKey: node.alias,
                  filterName: node.name,
                  searchValue: paths.join(' / '),
                  useDepth: false,
                  filterOptionNode: (
                    <TagOptionNode tag={node} parentNames={parents} />
                  ),
                };
              })
              .filter((item) => {
                if (!searchValue) return true;
                return item.searchValue
                  .toLocaleLowerCase()
                  .includes(searchValue);
              });
          }
          const lv1 = getLv1TagNodes(tagMap).map((t) => t.alias);
          const mapper = (alias: string): StructuredFilterItem => {
            const node = tagMap[alias];
            const paths = getTagPath(tagMap, alias).map((p) => p.name);
            const parents = paths.slice(0, -1);
            return {
              columnKey: FEEDBACK_COLUMNS.TAG,
              filterKey: node.alias,
              filterName: node.name,
              searchValue: paths.join(' / '),
              useDepth: node.children.length > 0,
              childOptions: {
                fetcher: async () => {
                  return node.children.map(mapper);
                },
              },
              filterOptionNode: (
                <TagOptionNode tag={node} parentNames={parents} />
              ),
            };
          };
          const tree = lv1.map(mapper);
          return tree;
        },
      },
    },
    [FEEDBACK_COLUMNS.SOURCE_INFO]: {
      icon: <Workflow />,
      columnKey: FEEDBACK_COLUMNS.SOURCE_INFO,
      columnName: 'Source',
      isMultiValueField: false,
      childField: sourceFilterMap,
    },
    [FEEDBACK_COLUMNS.CATEGORY]: {
      icon: <Archive />,
      columnKey: FEEDBACK_COLUMNS.CATEGORY,
      columnName: 'Category',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return OrderedCategoryList.map((id) => {
            const category = currentCategoryMap[Number(id) as CATEGORY_TYPE];
            return {
              columnKey: FEEDBACK_COLUMNS.CATEGORY,
              filterKey: category.id,
              filterName: category.name,
              searchValue: category.name,
              filterOptionNode: (
                <Label
                  text={category.name}
                  customColor={category.color}
                  themeColor={0}
                  isSelected={false}
                  withoutBorder
                  withoutHover
                  style={{ padding: 0 }}
                />
              ),
            };
          }).filter((item) => {
            if (!searchValue) return true;
            return item.searchValue.toLocaleLowerCase().includes(searchValue);
          });
        },
      },
    },

    [FEEDBACK_COLUMNS.PRIORITY]: {
      icon: <ArrowUpNarrowWide />,
      columnKey: FEEDBACK_COLUMNS.PRIORITY,
      columnName: 'Priority',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return Object.keys(priorityMap)
            .sort((a, b) => Number(b) - Number(a))
            .map((key) => {
              const PriorityIcon =
                priorityMap[Number(key) as PriorityKeyT].icon;
              const priorityText =
                priorityMap[Number(key) as PriorityKeyT].value;
              return {
                columnKey: FEEDBACK_COLUMNS.PRIORITY,
                filterKey: Number(key) as PriorityKeyT,
                filterName: priorityMap[Number(key) as PriorityKeyT].value,
                searchValue: priorityMap[Number(key) as PriorityKeyT].value,
                filterOptionNode: (
                  <IconTextProperty icon={PriorityIcon} value={priorityText} />
                ),
              };
            })
            .filter((item) => {
              if (!searchValue) return true;
              return item.searchValue.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [FEEDBACK_COLUMNS.TAXONOMY_TOPIC]: {
      icon: <BuildOutlined />,
      columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
      columnName: 'Taxonomy',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'tree',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          const shouldHideSavedTopicTaxonomy = false;

          const mapChildTopic = (
            topicMap: Record<string, Topic2WithoutCount<TOPIC_2_TYPE>>,
            topicAliases: string[],
            paths: string[],
          ): StructuredFilterItem[] => {
            return topicAliases.map((alias) => {
              const node = topicMap[alias];
              return {
                columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
                filterKey: node.alias,
                filterName: node.name,
                searchValue: paths.join(' / '),
                useDepth: node.children && node.children.length > 0,
                childOptions:
                  node.children && node.children.length
                    ? {
                        fetcher: async () =>
                          mapChildTopic(topicMap, node.children || [], [
                            ...paths,
                            node.name,
                          ]),
                      }
                    : undefined,
                filterOptionNode: (
                  <Flex g={8} a="center">
                    <MainText>{node.name}</MainText>
                  </Flex>
                ),
              };
            });
          };

          const topicTrees = Object.values(taxonomyMap);
          const result: StructuredFilterItem[] = [];

          if (searchValue) {
            const fullTopics = Object.values(fullTopicMap).filter((topic) => {
              if (!shouldHideSavedTopicTaxonomy) {
                return true;
              }

              return topic.taxonomy_id !== savedTaxonomyId;
            });
            fullTopics.forEach((topic) => {
              const path = `${taxonomyUtils
                .getTopicPath(topic)
                .map((t) => t.label)
                .join(' / ')} / ${topic.name}`;
              if (path.toLocaleLowerCase().includes(searchValue)) {
                result.push({
                  columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
                  filterKey: topic.alias,
                  filterName: topic.name,
                  searchValue: path,
                  filterOptionNode: (
                    <Flex g={8} a="center">
                      <Flex a="center" className="ticket-area">
                        {getIcon(topic.category)}
                      </Flex>
                      <MainText>{topic.name}</MainText>
                    </Flex>
                  ),
                });
              }
            });
          } else {
            for (const topicTree of topicTrees) {
              if (
                shouldHideSavedTopicTaxonomy &&
                topicTree.taxonomy.alias === savedTaxonomyId
              ) {
                continue;
              }

              const { taxonomy } = topicTree;
              const topics = Object.values(topicTree.topicMap);
              const rootTopics = topics.filter((topic) => topic.level === 1);
              const topicNodes = rootTopics.map((topic) => ({
                columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
                filterKey: topic.alias,
                filterName: topic.name,
                searchValue: `${taxonomy.title} / ${topic.name}`,
                useDepth: topic.children && topic.children.length > 0,
                childOptions:
                  topic.children && topic.children.length
                    ? {
                        fetcher: async () =>
                          mapChildTopic(fullTopicMap, topic.children || [], [
                            taxonomy.title,
                            topic.name,
                          ]),
                      }
                    : undefined,
                filterOptionNode: (
                  <Flex g={8} a="center">
                    <MainText>{topic.name}</MainText>
                  </Flex>
                ),
              }));

              result.push({
                columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
                filterKey: taxonomy.alias,
                filterName: taxonomy.title,
                disableCheck: true,
                filterOptionNode: <MainText># {taxonomy.title}</MainText>,
                childOptions: {
                  fetcher: async () => topicNodes,
                },
              });
            }
          }

          return result;
        },
      },
    },
    [FEEDBACK_COLUMNS.HASHTAG]: {
      icon: <Hash />,
      columnKey: FEEDBACK_COLUMNS.HASHTAG,
      columnName: 'Hashtag',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'custom',
        Component: StructuredHashtagFilterInput,
      },
    },
    [FEEDBACK_COLUMNS.KEYWORD]: {
      icon: <Hash />,
      columnKey: FEEDBACK_COLUMNS.KEYWORD,
      columnName: 'Keyword',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'custom',
        Component: StructuredKeywordFilterInput,
      },
    },
    [FEEDBACK_COLUMNS.MENTION]: {
      icon: <AtSign />,
      columnKey: FEEDBACK_COLUMNS.MENTION,
      columnName: 'Mention',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'custom',
        Component: StructuredMentionFilterInput,
      },
    },
    [FEEDBACK_COLUMNS.REMARKS]: {
      icon: <Asterisk />,
      columnKey: FEEDBACK_COLUMNS.REMARKS,
      columnName: 'Remarks',
      isMultiValueField: true,
      filterItemOptions: {
        type: 'pagination',
        filterItemFetcher: async (params) => {
          if (!params?.searchValue) return [];
          const res = await service.feedback.searchRemarks(params.searchValue);
          if (!res.remarks) return [];
          return res.remarks.map((remark) => ({
            columnKey: FEEDBACK_COLUMNS.REMARKS,
            filterKey: remark,
            filterName: remark,
            filterOptionNode: remark,
          }));
        },
      },
    },

    [FEEDBACK_COLUMNS.LANGUAGE]: {
      icon: <Globe />,
      columnKey: FEEDBACK_COLUMNS.LANGUAGE,
      columnName: 'Language',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return languageList
            .filter((language) => {
              if (!searchValue) return true;
              return (
                language.name.toLocaleLowerCase().includes(searchValue) ||
                language.code.toLocaleLowerCase().includes(searchValue)
              );
            })
            .map((language) => ({
              columnKey: FEEDBACK_COLUMNS.LANGUAGE,
              filterKey: language.code,
              filterName: language.name,
              searchValue: language.name,
              useDepth: false,
              filterOptionNode: language.name,
            }));
        },
      },
    },

    [FEEDBACK_COLUMNS.CONTENT_ORIGIN]: {
      icon: <LayoutList />,
      columnKey: FEEDBACK_COLUMNS.CONTENT_ORIGIN,
      columnName: 'Content origin',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          const contentOrigins = [
            { key: 'BODY', name: 'Body' },
            { key: 'COMMENT', name: 'Comment' },
            { key: 'MESSAGE', name: 'Message' },
          ];
          return contentOrigins
            .filter((item) => {
              if (!searchValue) return true;
              return item.name.toLocaleLowerCase().includes(searchValue);
            })
            .map((item) => ({
              columnKey: FEEDBACK_COLUMNS.CONTENT_ORIGIN,
              filterKey: item.key,
              filterName: item.name,
              searchValue: item.name,
              filterOptionNode: item.name,
            }));
        },
      },
    },

    [FEEDBACK_COLUMNS.TICKET_STATUS]: {
      icon: <TicketCheck />,
      columnKey: FEEDBACK_COLUMNS.TICKET_STATUS,
      columnName: 'Ticket status',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return [
            SOURCE_STATUS.CLOSE,
            SOURCE_STATUS.OPEN,
            SOURCE_STATUS.PENDING,
          ]
            .map((status) => ({
              columnKey: FEEDBACK_COLUMNS.TICKET_STATUS,
              filterKey: status,
              filterName: feedbackUtils.getSourceStatusLabel(status).value,
              filterOptionNode: (
                <IconTextProperty
                  {...feedbackUtils.getSourceStatusLabel(status)}
                />
              ),
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [FEEDBACK_COLUMNS.PRODUCT]: {
      icon: <Package />,
      columnKey: FEEDBACK_COLUMNS.PRODUCT,
      columnName: 'Product info',
      isMultiValueField: true,
      childField: productFilterMap,
    },
    [FEEDBACK_COLUMNS.SCORE]: {
      icon: <CircleStar />,
      columnKey: FEEDBACK_COLUMNS.SCORE,
      columnName: 'Score',
      isMultiValueField: false,
      childField: scoreFilterMap,
    },
    [FEEDBACK_COLUMNS.ENGAGEMENT]: {
      icon: <ChartNoAxesCombined />,
      columnKey: FEEDBACK_COLUMNS.ENGAGEMENT,
      columnName: 'Metric',
      isMultiValueField: false,
      childField: engagementFilterMap,
    },
    [FEEDBACK_COLUMNS.AD_PREDICTION]: {
      icon: <ScanSearch />,
      columnKey: FEEDBACK_COLUMNS.AD_PREDICTION,
      columnName: 'Ad prediction',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async () => {
          return Array.from(adPredictionList.entries()).map(([key, value]) => ({
            columnKey: FEEDBACK_COLUMNS.AD_PREDICTION,
            filterKey: key,
            filterName: value,
            searchValue: value,
            useDepth: false,
            filterOptionNode: value,
          }));
        },
      },
    },
    [FEEDBACK_COLUMNS.SENTIMENT]: {
      icon: <Heart />,
      columnKey: FEEDBACK_COLUMNS.SENTIMENT,
      columnName: 'Sentiments',
      isMultiValueField: false,
      childField: sentimentFilterMap,
    },

    [FEEDBACK_COLUMNS.STATUS]: {
      columnKey: FEEDBACK_COLUMNS.STATUS,
      columnName: 'AI Status',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return [FEEDBACK_STATUS.FULL, FEEDBACK_STATUS.SENTIMENT_ONLY]
            .map((status) => ({
              columnKey: FEEDBACK_COLUMNS.STATUS,
              filterKey: status,
              filterName: feedbackUtils.getFeedbackStatusLabel(status),
              filterOptionNode: feedbackUtils.getFeedbackStatusLabel(status),
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [FEEDBACK_COLUMNS.SNIPPET]: {
      columnKey: FEEDBACK_COLUMNS.SNIPPET,
      columnName: 'Text',
      isMultiValueField: true,
      hide: true,
    },
    [FEEDBACK_COLUMNS.FEEDBACK_TEXT]: {
      columnKey: FEEDBACK_COLUMNS.FEEDBACK_TEXT,
      columnName: 'Feedback Text',
      isMultiValueField: true,
      hide: true,
    },
    [FEEDBACK_COLUMNS.ENTITY_GROUP]: {
      columnKey: FEEDBACK_COLUMNS.ENTITY_GROUP,
      columnName: 'Entity Group',
      isMultiValueField: true,
      hide: true,
    },
    [FEEDBACK_COLUMNS.SIGNAL_ISSUE_NAME]: {
      columnKey: FEEDBACK_COLUMNS.SIGNAL_ISSUE_NAME,
      columnName: 'Issue',
      isMultiValueField: true,
      hide: false,
    },

    // [ENGAGEMENT_COLUMNS.TOTAL_ENGAGEMENT]: {
    //   icon: <Sigma />,
    //   columnKey: ENGAGEMENT_COLUMNS.TOTAL_ENGAGEMENT,
    //   columnName: getSocialAwareLabel(
    //     ENGAGEMENT_COLUMNS.TOTAL_ENGAGEMENT,
    //     'Total Engagement',
    //   ),
    //   isMultiValueField: false,
    //   filterItemOptions: {
    //     type: 'range',
    //     RangeComponent: RangeFilter,
    //   },
    // },
    [FEEDBACK_COLUMNS.IMPRESSION]: {
      icon: <Zap />,
      columnKey: FEEDBACK_COLUMNS.IMPRESSION,
      columnName: 'Impression',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'range',
        RangeComponent: RangeFilter,
      },
    },
  };

  return fieldOptions;
};

export default useStructuredFilterMap;
